<template>
  <!-- 拖拽上传 -->
  <a-upload-dragger
    v-if="operateStyle === 'drag'"
    v-model:file-list="currentFileList"
    :accept="currentAccept"
    v-bind="attrs"
    :before-upload="beforeUpload"
    :list-type="currentListType"
    :class="[bem.b(), bem.is('drag'), disabled ? bem.is('disabled') : '']"
    :disabled="disabled"
    @change="handleChange"
  >
    <ll-icon :icon-name="uploadIcon || 'icon-upload'" :size="uploadIconSize" />
    <!-- 上传标题 -->
    <div v-if="uploadTitle" :class="bem.m('upload-title')">
      {{ uploadTitle }}
    </div>
    <!-- 上传提示 -->
    <div v-if="uploadTip" :class="bem.m('upload-tip')">
      {{ uploadTip }}
    </div>
  </a-upload-dragger>

  <!-- 默认上传 -->
  <a-upload
    v-else
    v-bind="attrs"
    v-model:file-list="currentFileList"
    :accept="currentAccept"
    :before-upload="beforeUpload"
    :class="[bem.b(), bem.is('default')]"
    :list-type="currentListType"
    :disabled="disabled"
    @dragenter.capture.stop
    @dragover.capture.stop
    @drop.capture.stop
    @change="handleChange"
  >
    <!-- 卡片风格 -->
    <div v-if="operateStyle === 'card'" :class="bem.is('card')">
      <ll-icon
        :icon-name="uploadIcon || 'icon-upload'"
        :size="uploadIconSize"
      />
      <div v-if="uploadTitle" :class="bem.m('upload-title')">
        {{ uploadTitle }}
      </div>
    </div>

    <!-- 按钮风格 -->
    <div v-else :class="bem.is('button')">
      <ll-button :disabled="disabled">
        <ll-icon
          :icon-name="uploadIcon || 'icon-upload'"
          :size="uploadIconSize"
        />
        <div v-if="uploadTitle" :class="bem.m('upload-title')">
          {{ uploadTitle }}
        </div>
      </ll-button>
      <!-- 上传提示 -->
      <div v-if="uploadTip" :class="bem.m('upload-tip')">
        {{ uploadTip }}
      </div>
    </div>
    <template v-for="item in Object.keys($slots)" #[item]="data">
      <slot :name="item" v-bind="data || {}"></slot>
    </template>
  </a-upload>

  <!-- 上传提示 -->
  <div v-if="uploadTip && operateStyle === 'card'" :class="bem.m('upload-tip')">
    {{ uploadTip }}
  </div>
</template>

<script lang="ts" setup>
import { createNamespace, formatFileSize } from '@ll-plus/utils'
import { computed, ref, useAttrs } from 'vue'

import { matchAcceptType } from '@ll-plus/utils'
import { message } from 'ant-design-vue'
import { FileType } from 'ant-design-vue/es/upload/interface'
import { uploadProps } from './upload'

defineOptions({ name: 'LlUpload' })

const attrs = useAttrs()
const bem = createNamespace('upload')

const props = defineProps(uploadProps)

const emits = defineEmits(['update:fileList', 'change'])

const rejectUid = ref<string[]>([])

const currentFileList = computed({
  get: () => props.fileList,
  set: (val) => {
    emits('update:fileList', val)
    emits('change', val)
  },
})

const currentAccept = computed(() => {
  return props.accept
})

const beforeUpload = async (file: FileType, fileList: FileType[]) => {
  let isType = true
  let isSize = true
  if (!matchAcceptType(file, currentAccept.value)) {
    message.error(`${file.name} 需要为${currentAccept.value}格式。`)
    isType = false
  }
  if (props.imgSize > 0) {
    const fileSizeInMB = file.size / (1024 * 1024)
    if (fileSizeInMB > props.imgSize) {
      message.error(
        `${file.name} 文件不得大于${formatFileSize(
          props.imgSize * 1024 * 1024,
        )}。`,
      )
      isSize = false
    }
  }

  if (!(isType && isSize)) {
    rejectUid.value.push(file.uid)
    return Promise.reject(false)
  }
  if (!props.beforeUpload?.(file, fileList)) {
    rejectUid.value.push(file.uid)
    return Promise.reject(false)
  }
  return Promise.resolve()
}

const handleChange = ({
  fileList,
}: {
  file: FileType
  fileList: FileType[]
}) => {
  const files = fileList.filter(
    (item) => rejectUid.value.indexOf(item.uid) === -1,
  )
  currentFileList.value = files
}

const currentListType = computed(() => {
  // 若当前为卡片类型，则设置上传列表为缩略图
  return props.operateStyle === 'card' ? 'picture-card' : props.listType
})
</script>
